import { ChangeDetectorRef, Component, ComponentFactoryResolver, OnDestroy, OnInit } from '@angular/core';
import { JeevesProductCardComponent, JeevesProductGridViewComponent } from 'gung-standard-jeeves';
import {
  PriceService,
  GungFlowService,
  Product,
  AuthService,
  PriceConfigService,
  LocationConfigService,
  GridViewProductCard,
  GungAnonymousConfigService
} from 'gung-standard';
import { forkJoin, Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ColmecCustomerProductPrice } from '../../../models/colmec-price';
import { Router, ActivatedRoute } from '@angular/router';
import { first } from 'rxjs';

@Component({
    selector: 'colmec-jeeves-product-card',
    templateUrl: './colmec-jeeves-product-card.component.html',
    styleUrls: ['./colmec-jeeves-product-card.component.scss'],
    standalone: false
})
export class ColmecJeevesProductCardComponent
  extends JeevesProductGridViewComponent /* JeevesProductCardComponent */
  implements OnInit, OnDestroy
{
  public hidePrice = false;

  constructor(
    protected priceService: PriceService,
    protected gungFlowService: GungFlowService,
    protected authService: AuthService,
    protected gungAnonymousConfigService: GungAnonymousConfigService,
    protected router: Router,
    protected locationConfigService: LocationConfigService,
    private priceConfigService: PriceConfigService,
    protected translateService: TranslateService
  ) {
    super(priceService, gungFlowService, authService, gungAnonymousConfigService, router, locationConfigService);
  }

  ngOnInit() {
    super.ngOnInit();

    this.priceConfigService.hidePrice.subscribe(hidePrice => (this.hidePrice = hidePrice));
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  getNetPriceLabel(product: Product): Observable<string> {
    if (!product) {
      return of('');
    }

    if (product.extra.ar.varugruppkod === '10') {
      return of(this.translateService.instant('NET_PRICE_INC_FRAME'));
    }

    return of(this.translateService.instant('NET_PRICE'));
  }

  getNetPriceValue(price: ColmecCustomerProductPrice): number {
    if (price.priceInclAutoAdded) {
      return price.priceInclAutoAdded.value;
    }

    return price.customerNetPrice.value;
  }

  protected subscribeToProducts() {
    if (!this.oldFlow || !this.currentFlow || this.oldFlow.id !== this.currentFlow.id) {
      // remove cached data;
      this.keyedMapData = {};
      this.oldFlow = this.currentFlow;
    }
    const newIds = this.data.map(p => p.id).filter(id => !Object.keys(this.keyedMapData).includes(id));
    const newIdsWithouConcept = this.data
      .filter(p => p.productType === 'singleDimension')
      .map(p => p.id)
      .filter(id => !Object.keys(this.keyedMapData).includes(id));
    if (newIds.length === 0) {
      this.readMappedDataFromCache();
      return;
    }

    const subscription = forkJoin([
      of(this.data),
      this.priceService.getCurrentCustomerPrices(newIdsWithouConcept).pipe(first()),
      this.gungFlowService.getSelectedFlow().pipe(first())
    ])
      .pipe(first())
      .subscribe(data => {
        const productData = data[0];
        const prices = data[1];
        let avs = [];
        this.includeAvailability = data[2].useAvailabilities || data[2].requireAvailability;
        if (this.includeAvailability) {
          avs = newIdsWithouConcept.map(id =>
            this.getProductAvailability(this.data.find(product => product.id === id))
          );
        }

        this.keyedMapData = {
          ...this.keyedMapData,
          ...newIds.reduce((acc, curr) => {
            const product = productData.filter(p => p && p.id === curr)[0];

            if (!product) {
              this.findError = true;
              this.errorMessage += 'No product found. ';
              throw new Error('No product found');
            }

            const price = prices.filter(p => p && p.productId === curr)[0];
            if (!price) {
              this.findError = true;
              this.errorMessage += 'No price found.';
              if (newIdsWithouConcept.includes(curr)) {
                throw new Error('No price found');
              }
            }

            const av = avs.filter(p => p && p.productId === curr)[0];
            if (!av && this.includeAvailability) {
              this.findError = true;
              this.errorMessage += 'No availability found. ';
              if (newIdsWithouConcept.includes(curr)) {
                throw new Error('No availability found');
              }
            }
            const item: GridViewProductCard = this.mapItem(curr, product, price, av);
            return {
              ...acc,
              [curr]: item
            };
          }, {})
        };
        this.readMappedDataFromCache();
      });
    this.subscriptions.push(subscription);
  }

  goToDetailConcept(product: Product): void {
    this.router.navigate(['/concept/', product.id]);
  }

}

